// Meeting transcript: the rows and their editors. Owner: WP-6 (BUILD 1).
//
// Each row shows who said it, resolved from the speaker table (the row's own
// person, else the voice's person, else "Speaker N"), and opens the "Who said
// this?" popover. Short replies from another speaker (1–3 words, starting
// inside the running row or within 1 s of it) fold into that row as
// reactions and stay citable. A row can be split at a word ("Split here",
// then who said the rest), its text edited (text only: speaker changes go
// through the popover and always name their scope), and recording gaps show
// as markers where they happened. Each row's text is rendered by
// `renderText(segment, raw)`, WP-8's repair slot. An uncertain row gets
// BUILD 2's voice flag (meeting-voice.tsx, WP2-4), and its popover highlights
// the voice the row sounds like, never preselecting it. Props: meeting-slots.ts.
import './meeting-speakers.css';
import { useEffect, useMemo, useState } from 'react';
import { formatCopy } from '@/i18n/ns';
import { useWorkspace } from './context';
import { meetingsApi } from './meetings-api';
import {
  clockLabel,
  speakerIndex,
  transcriptItems,
  type TranscriptRow,
} from './meeting-speaker-model';
import {
  announceSpeakerChange,
  failureCopy,
  MeetingSpeakerPopover,
  participantFor,
  SpeakerTargetPicker,
  speakerName,
  useCompanyMembers,
  useSpeakersCopy,
  type SpeakersCopy,
  type SpeakerTarget,
} from './meeting-speaker-popover';
import type { MeetingPlayerRef, MeetingSegmentText, MeetingTranscriptProps } from './meeting-slots';
import { RowVoiceFlag } from './meeting-voice';
import type { CompanyMeeting, CompanyMember, MeetingSegment, MeetingWord } from './types';

type Mode = 'speaker' | 'split' | 'edit';
type Open = { index: number; mode: Mode } | null;

function seek(player: MeetingPlayerRef, startMs: number | null) {
  if (!player.current || startMs === null) return;
  player.current.currentTime = startMs / 1000;
  void player.current.play().catch(() => {});
}

function TextEditor({
  meeting,
  segment,
  copy,
  onClose,
}: {
  meeting: CompanyMeeting;
  segment: MeetingSegment;
  copy: SpeakersCopy;
  onClose: () => void;
}) {
  const ws = useWorkspace();
  const [text, setText] = useState(segment.displayText);
  const [saving, setSaving] = useState(false);
  async function save() {
    const value = text.trim();
    if (!value || value === segment.displayText) return onClose();
    setSaving(true);
    try {
      Object.assign(
        segment,
        await meetingsApi.updateSegment(meeting.id, segment.index, { displayText: value }),
      );
      announceSpeakerChange(meeting.id);
      onClose();
    } catch {
      ws.notify(copy.editFailed);
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="ws-transcript-editor">
      <textarea value={text} onChange={(event) => setText(event.target.value)} />
      <button className="ws-button primary" disabled={saving} onClick={() => void save()}>
        {saving ? copy.saving : copy.save}
      </button>
      <button className="ws-button" onClick={onClose}>
        {copy.cancel}
      </button>
    </div>
  );
}

type SplitTail = { participantId?: string; label?: string };

async function tailFor(meetingId: string, target: SpeakerTarget): Promise<SplitTail> {
  if (target.kind === 'same' || target.kind === 'not_participant') return {};
  if (target.kind === 'voice') return { label: target.label };
  return { participantId: (await participantFor(meetingId, target)) as string };
}

function SplitWords({
  words,
  at,
  onPick,
}: {
  words: MeetingWord[];
  at: number | null;
  onPick: (index: number) => void;
}) {
  return (
    <p className="ws-split-words">
      {words.map((word, index) => (
        <button
          key={`${word[0]}-${index}`}
          aria-pressed={index === at}
          disabled={index === 0}
          onClick={() => onPick(index)}
        >
          {word[2]}
        </button>
      ))}
    </p>
  );
}

/** The row's word timings: list and detail reads leave them out. */
function useWords(meetingId: string, index: number) {
  const [words, setWords] = useState<MeetingWord[] | null>(null);
  useEffect(() => {
    let live = true;
    void meetingsApi
      .segment(meetingId, index)
      .then((segment) => live && setWords(segment.words ?? []))
      .catch(() => live && setWords([]));
    return () => {
      live = false;
    };
  }, [meetingId, index]);
  return words;
}

function SplitEditor({
  meeting,
  segment,
  members,
  copy,
  onClose,
}: {
  meeting: CompanyMeeting;
  segment: MeetingSegment;
  members: CompanyMember[];
  copy: SpeakersCopy;
  onClose: () => void;
}) {
  const ws = useWorkspace();
  const words = useWords(meeting.id, segment.index);
  const [at, setAt] = useState<number | null>(null);
  const [asking, setAsking] = useState(false);
  async function split(target: SpeakerTarget) {
    try {
      const tail = await tailFor(meeting.id, target);
      await meetingsApi.splitSegment(meeting.id, segment.index, { atWord: at as number, ...tail });
      announceSpeakerChange(meeting.id);
      onClose();
    } catch (failure) {
      ws.notify(failureCopy(failure, copy));
    }
  }
  if (words === null) return <p className="ws-muted">…</p>;
  if (words.length < 2)
    return (
      <div className="ws-speaker-popover" role="note">
        <p>{copy.splitNeedsWords}</p>
        <button className="ws-citation" onClick={onClose}>
          {copy.cancel}
        </button>
      </div>
    );
  return (
    <div className="ws-speaker-popover" role="dialog" aria-label={copy.split}>
      {asking ? (
        <>
          <strong>{copy.whoSaidRest}</strong>
          <SpeakerTargetPicker
            meeting={meeting}
            members={members}
            copy={copy}
            options={{ same: true, voices: { except: null } }}
            onPick={(target) => void split(target)}
          />
        </>
      ) : (
        <>
          <p className="ws-muted">{copy.pickSplitWord}</p>
          <SplitWords words={words} at={at} onPick={setAt} />
          <button
            className="ws-button primary"
            disabled={at === null}
            onClick={() => setAsking(true)}
          >
            {copy.splitHere}
          </button>
        </>
      )}
      <button className="ws-citation" onClick={onClose}>
        {copy.cancel}
      </button>
    </div>
  );
}

/** Folded replies: each keeps its anchor, and its name opens "Who said this?". */
function Reactions({
  row,
  raw,
  open,
  copy,
  renderText,
  onOpen,
}: {
  row: TranscriptRow;
  raw: boolean;
  open: number | null;
  copy: SpeakersCopy;
  renderText: MeetingSegmentText;
  onOpen: (index: number | null) => void;
}) {
  if (!row.reactions.length) return null;
  return (
    <span className="ws-transcript-reactions">
      {row.reactions.map(({ segment, speaker }) => (
        <span
          key={segment.index}
          id={`meeting-segment-${segment.index}`}
          className="ws-transcript-reaction"
        >
          <button
            className="ws-citation"
            aria-expanded={open === segment.index}
            title={copy.whoSaidThis}
            onClick={() => onOpen(open === segment.index ? null : segment.index)}
          >
            {formatCopy(copy.reaction, { name: speakerName(speaker, copy) })}
          </button>{' '}
          {renderText(segment, raw)}
        </span>
      ))}
    </span>
  );
}

function RowTools({
  mode,
  copy,
  onOpen,
  editable,
}: {
  mode: Mode | null;
  copy: SpeakersCopy;
  onOpen: (mode: Mode) => void;
  editable: boolean;
}) {
  if (mode || !editable) return null;
  return (
    <>
      <button className="ws-citation" onClick={() => onOpen('edit')}>
        {copy.edit}
      </button>
      <button className="ws-citation" onClick={() => onOpen('split')}>
        {copy.split}
      </button>
    </>
  );
}

const correctionLabel = (kind: string, copy: SpeakersCopy) =>
  kind === 'company_term' ? copy.repairedCompany : copy.repairedPoneva;

function TranscriptRowView({
  meeting,
  row,
  raw,
  player,
  renderText,
  correctionKind,
  mode,
  reaction,
  members,
  copy,
  onOpen,
  onReaction,
  editable,
}: {
  meeting: CompanyMeeting;
  row: TranscriptRow;
  raw: boolean;
  player: MeetingPlayerRef;
  renderText: MeetingSegmentText;
  correctionKind: string | null;
  mode: Mode | null;
  /** The folded reply whose popover is open, if any. */
  reaction: MeetingSegment | null;
  members: CompanyMember[];
  copy: SpeakersCopy;
  onOpen: (mode: Mode | null) => void;
  onReaction: (index: number | null) => void;
  editable: boolean;
}) {
  const { segment, speaker } = row;
  const close = () => onOpen(null);
  return (
    <article className="ws-transcript-segment" id={`meeting-segment-${segment.index}`}>
      <button
        className="ws-transcript-time"
        disabled={segment.startMs === null}
        onClick={() => seek(player, segment.startMs)}
      >
        {clockLabel(segment.startMs)}
      </button>
      <div>
        <div className="ws-transcript-speaker">
          {editable ? (
            <button
              className={`ws-transcript-speaker-name ${speaker.confirmed ? 'confirmed' : 'unconfirmed'}`}
              aria-expanded={mode === 'speaker'}
              title={copy.whoSaidThis}
              onClick={() => onOpen(mode === 'speaker' ? null : 'speaker')}
            >
              {speakerName(speaker, copy)}
            </button>
          ) : (
            <span
              className={`ws-transcript-speaker-name ${speaker.confirmed ? 'confirmed' : 'unconfirmed'}`}
            >
              {speakerName(speaker, copy)}
            </span>
          )}
          {speaker.kind === 'not_participant' && <small>{copy.notParticipant}</small>}
          {editable && (
            <RowVoiceFlag meeting={meeting} segment={segment} onOpen={() => onOpen('speaker')} />
          )}
          <RowTools mode={mode} copy={copy} onOpen={onOpen} editable={editable} />
        </div>
        {editable && mode === 'speaker' && (
          <MeetingSpeakerPopover
            meeting={meeting}
            segment={segment}
            members={members}
            hint={segment.voice?.uncertain ? segment.voice.bestOtherLabel : undefined}
            onClose={close}
          />
        )}
        {editable && mode === 'split' && (
          <SplitEditor
            meeting={meeting}
            segment={segment}
            members={members}
            copy={copy}
            onClose={close}
          />
        )}
        {editable && mode === 'edit' ? (
          <TextEditor meeting={meeting} segment={segment} copy={copy} onClose={close} />
        ) : (
          <div className="ws-transcript-copy">
            {renderText(segment, raw)}
            <Reactions
              row={row}
              raw={raw}
              open={reaction?.index ?? null}
              copy={copy}
              renderText={renderText}
              onOpen={onReaction}
            />
          </div>
        )}
        {editable && reaction && (
          <MeetingSpeakerPopover
            meeting={meeting}
            segment={reaction}
            members={members}
            onClose={() => onReaction(null)}
          />
        )}
        {correctionKind && !raw && (
          <small className="ws-repair-label">{correctionLabel(correctionKind, copy)}</small>
        )}
      </div>
    </article>
  );
}

const openReaction = (row: TranscriptRow, open: Open) =>
  open?.mode === 'speaker'
    ? (row.reactions.find((reply) => reply.segment.index === open.index)?.segment ?? null)
    : null;

export function MeetingTranscript({
  meeting,
  player,
  raw,
  onRawChange,
  renderText,
}: MeetingTranscriptProps) {
  const copy = useSpeakersCopy();
  const [open, setOpen] = useState<Open>(null);
  const members = useCompanyMembers(open !== null && open.mode !== 'edit');
  const editable = meeting.canEdit !== false;
  const items = useMemo(() => transcriptItems(meeting, speakerIndex(meeting)), [meeting]);
  const correctionKinds = useMemo(
    () =>
      new Map(
        meeting.corrections
          // A member's own edits (text edits, splits) are not repairs.
          ?.filter(
            (item) =>
              item.kind !== 'manual' &&
              (item.evidence as { actor?: string } | null)?.actor !== 'member',
          )
          .map((item) => [item.segmentIndex, item.kind]) ?? [],
      ),
    [meeting.corrections],
  );
  return (
    <section className="ws-transcript">
      <header>
        <div>
          <div className="ws-eyebrow">{copy.transcriptEyebrow}</div>
          <h2>{copy.transcriptTitle}</h2>
        </div>
        <div className="ws-view-tabs">
          <button className={!raw ? 'active' : ''} onClick={() => onRawChange(false)}>
            {copy.viewRepaired}
          </button>
          {editable && (
            <button className={raw ? 'active' : ''} onClick={() => onRawChange(true)}>
              {copy.viewRaw}
            </button>
          )}
        </div>
      </header>
      <p className="ws-page-note">{copy.transcriptNote}</p>
      {items.map((item) =>
        item.kind === 'gap' ? (
          <div key={`gap-${item.gap.atMs}`} className="ws-transcript-gap" role="note">
            {formatCopy(copy.gapMarker, { duration: clockLabel(item.gap.lostMs) })}
          </div>
        ) : (
          <TranscriptRowView
            key={item.segment.index}
            meeting={meeting}
            row={item}
            raw={raw}
            player={player}
            renderText={renderText}
            correctionKind={correctionKinds.get(item.segment.index) ?? null}
            mode={open?.index === item.segment.index ? open.mode : null}
            reaction={openReaction(item, open)}
            members={members}
            copy={copy}
            onOpen={(mode) => setOpen(mode ? { index: item.segment.index, mode } : null)}
            onReaction={(index) => setOpen(index === null ? null : { index, mode: 'speaker' })}
            editable={editable}
          />
        ),
      )}
    </section>
  );
}
